import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import React, { useEffect, useState } from "react";
import { useAPIEditServiceKendaraan } from "../api/api-edit-service-kendaraan";
import {
  genElDate,
  genElNumber,
  genElObject,
  genElText,
} from "../../../components/utils/EditorDialogElementGenerator";
import { ProgressSpinner } from "primereact/progressspinner";
import ServiceSelectorDialog from "../../../dialogs/selectors/ServiceSelectorDialog";
import KendaraanSelectorDialog from "../../../dialogs/selectors/KendaraanSelectorDialog";
import { Divider } from "primereact/divider";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { genTemplateBodyText } from "../../../components/utils/TableTemplateBodyGenerator";
import useIsMobile from "../../../hooks/useIsMobile";


const ServiceKendaraanEdit = (props) => {
  const isMobile = useIsMobile();
  const { visible, onHide, editorData, onConfirmed } = props;

  const [showServiceSelector, setShowServiceSelector] = useState(false);
  const [showKendaraanSelector, setShowKendaraanSelector] = useState(false);

  const {
    loading,
    data,
    setData,
    setError,
    fetchDataOrCreateNew,
    error,
    handleOnConfirm,
  } = useAPIEditServiceKendaraan({ onConfirmed });

  // use effect section

  useEffect(() => {
    if (visible) {
      fetchDataOrCreateNew({ id: editorData.id, extraData: editorData });
    } else {
      setData({});
      setError({});
    }
  }, [visible]);

  useEffect(() => {
    const fieldsToCheck = [
      { key: "kendaraan_id", isValid: data.kendaraan_id > 0 },
      { key: "speedo_akhir", isValid: data.speedo_akhir > 0 },
    ];

    setError((prevError) => {
      const newError = { ...prevError };

      fieldsToCheck.forEach((f) => {
        if (f.isValid) {
          delete newError[f.key];
        }
      });

      return newError;
    });
  }, [data]);

  // handle ui event section

  const handleOnConfirmService = (event, datas) => {
    if (datas && datas.length > 0) {
      // existing services
      const existing = data?.data_history_service_detils ?? [];

      // remove duplicates
      const filtered = existing.filter(
        (item) => !datas.some((s) => s.id === item.service_id)
      );

      // push new
      const newServices = datas.map((service) => ({
        service_id: service.id,
        data_service: service,
      }));

      setData((prevData) => ({
        ...prevData,
        data_history_service_detils: [...filtered, ...newServices],
      }));

      setShowServiceSelector(false);
    }
  };

  const handleOnConfirmKendaraan = (event, datas) => {
    if (datas && datas.length > 0) {
      const kendaraan = datas[0];
      setData((prevData) => ({
        ...prevData,
        data_kendaraan: kendaraan,
        kendaraan_id: kendaraan.id,
      }));
      setShowKendaraanSelector(false);
    }
  };

  // genereate editor element section

  const elTanggal = genElDate({
    field: "tanggal_transaksi",
    name: "Tanggal",
    value: data.tanggal_transaksi,
    data,
    setData,
    error,
    primeFormat: "dd MM yy",
  });

  const elNomorPolisi = genElObject({
    field: "kendaraan_id",
    name: "No Polisi",
    value: data.data_kendaraan ? data.data_kendaraan.nomor_polisi : "",
    error,
    onClick: () => {
      setShowKendaraanSelector(true);
    },
  });

  const elKeterangan = genElText({
    field: "keterangan",
    name: "Keterangan",
    value: data.keterangan ?? "",
    data,
    setData,
    error,
  });

  const elSpeedometer = genElNumber({
    field: "speedo_akhir",
    name: "KM Terkini",
    data,
    setData,
    error,
  });

  const elLastKM = genElNumber({
    field: "data_kendaraan.speedo_akhir",
    value: data.data_kendaraan ? data.data_kendaraan.speedo_akhir : 0,
    data,
    setData,
    showButtons: false,
    error,
    disabled: true,
  });

  const dialogFooter = (
    <React.Fragment>
      <Button
        label="Cancel"
        icon="pi pi-times"
        outlined
        onClick={props.onHide}
      />
      <Button
        label="Save"
        icon="pi pi-check"
        onClick={(e) => {
          handleOnConfirm();
        }}
      />
    </React.Fragment>
  );

  const serviceBodyTemplate = (row) => {
    return genTemplateBodyText({
      row: row,
      field: "data_service.nama",
    });
  };

  const handleDeleteService = (row) => {
    const newData = data.data_history_service_detils.filter(
      (item) => item.service_id !== row.service_id
    );

    setData((prevData) => ({
      ...prevData,
      data_history_service_detils: newData,
    }));
  };

  const actionBodyTemplate = (row) => {
    return (
      <div className="row-center-center gap-2">
        <Button
          icon="pi pi-trash"
          rounded
          severity="danger"
          className="bg-red-400 max-w-[1.85rem] max-h-[1.85rem]"
          onClick={() => {
            handleDeleteService(row);
          }}
          pt={{ icon: { className: "!text-xs" } }}
        />
      </div>
    );
  };

  return (
    <Dialog
      visible={visible}
      style={{ width: "60rem" }}
      breakpoints={{ "960px": "75vw", "641px": "90vw" }}
      header={data.id && data.id > 0 ? "Edit Service" : "Tambah Service"}
      modal
      className={`p-fluid`}
      footer={dialogFooter}
      onHide={onHide}
      contentClassName="py-4 !pb-6"
      pt={{ footer: { className: "py-4" }, header: { className: "py-4" } }}
    >
      <ServiceSelectorDialog
        title="Pilih Service"
        visible={showServiceSelector}
        multiple={true}
        onHide={() => setShowServiceSelector(false)}
        onConfirm={handleOnConfirmService}
        additionalFilter={`id:notin:${(data.data_history_service_detils ?? [])
          .map((item) => item.service_id)
          .join(",")}`}
      />

      <KendaraanSelectorDialog
        title={"Pilih Kendaraan"}
        visible={showKendaraanSelector}
        onHide={() => setShowKendaraanSelector(false)}
        onConfirm={handleOnConfirmKendaraan}
      />

      <div className="col-start-center relative w-100 h-100">
        <div className={`${isMobile ? "col-start-start" : "row-start-start"} gap-2`}>
          <div className="col-start-start gap-3">
            {elTanggal}
            {elNomorPolisi}
            <div className="col-start-start w-100 gap-1">
              <label className="font-bold text-sm text-red-500">
                KM Sebelumnya
              </label>
              {elLastKM}
            </div>
            {elSpeedometer}
            {elKeterangan}
          </div>
          <Divider layout={`${isMobile ? "horizontal" : "vertical h-[19rem]"}`} />
          <div className={`${isMobile ? "col-start-start" : "col-start-end"} gap-2`}>
            <div className="row-start-start">
              <Button
                label="Tambah Service"
                icon="pi pi-plus"
                className="w-100"
                onClick={() => {
                  setShowServiceSelector(true);
                }}
                disabled={!data.kendaraan_id || data.kendaraan_id <= 0}
              />
            </div>

            <DataTable
              dataKey="service_id"
              className="w-100"
              loading={loading}
              showGridlines
              stripedRows
              value={data.data_history_service_detils ?? []}
            >
              <Column
                field=""
                header="Action"
                alignHeader={"center"}
                headerClassName="w-[5rem]"
                body={actionBodyTemplate}
              />
              <Column header="Service" headerClassName="w-[22rem]" body={serviceBodyTemplate} />
            </DataTable>
          </div>
        </div>
        {loading && (
          <div
            className={`col-center-center w-100  gap-2 absolute z-100 h-100 bg-black/20 rounded `}
          >
            <ProgressSpinner />
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default ServiceKendaraanEdit;
